import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "link";
type Size = "sm" | "md" | "lg";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Optional leading icon — keep small (h-4). */
  leading?: React.ReactNode;
  /** Optional trailing icon. */
  trailing?: React.ReactNode;
}

/**
 * Button system. Five variants, three sizes, all use the same focus
 * ring + active-press transform for cohesion.
 *
 *   primary   — brass on near-black ink. Single CTA per surface.
 *   secondary — surface-2 with line border, hovers to line-strong.
 *   ghost     — borderless, hovers to surface-2/60.
 *   danger    — muted bad red on faint bg.
 *   link      — inline text + underline, no surface.
 */

const base =
  "inline-flex items-center justify-center gap-2 rounded-md font-medium tracking-tight transition focus-ring " +
  "active:translate-y-[0.5px] disabled:cursor-not-allowed disabled:opacity-50";

const variants: Record<Variant, string> = {
  primary:
    "bg-accent text-accent-ink hover:bg-accent/90 hover:shadow-elev " +
    "border border-accent/0",
  secondary:
    "border border-line bg-surface-2/60 text-foreground hover:bg-surface-2 " +
    "hover:border-line-strong",
  ghost:
    "text-foreground/80 hover:bg-surface-2/60 hover:text-foreground " +
    "border border-transparent",
  danger:
    "border border-bad/40 bg-bad/10 text-bad hover:bg-bad/15 hover:border-bad/60",
  link:
    "text-accent underline-offset-4 hover:underline px-0 py-0 rounded-none " +
    "border-none bg-transparent",
};

const sizes: Record<Size, string> = {
  sm: "h-7 px-2.5 text-xs",
  md: "h-9 px-3.5 text-sm",
  lg: "h-11 px-5 text-sm",
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant = "primary",
      size = "md",
      loading,
      className,
      children,
      disabled,
      leading,
      trailing,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        base,
        variants[variant],
        variant !== "link" && sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : leading}
      {children}
      {!loading && trailing}
    </button>
  ),
);
Button.displayName = "Button";

function Spinner() {
  return (
    <svg
      className="h-3.5 w-3.5 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden
    >
      <circle cx={12} cy={12} r={10} className="opacity-25" />
      <path d="M12 2a10 10 0 0 1 10 10" className="opacity-100" />
    </svg>
  );
}

/** Display-only keyboard chord (e.g. ⌘K). For real shortcut wiring see
 *  `useKeyboardShortcuts`. Renders a styled <kbd>. */
export function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-line bg-surface px-1 font-mono text-[10px] font-medium text-foreground/70">
      {children}
    </kbd>
  );
}
